// Entry point for the React application

import React from 'react';
import ReactDOM from 'react-dom/client';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import App from './App';
import './i18n';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { MessageHandlerProvider } from './contexts/MessageContext';
import { ChatSessionProvider } from './contexts/ChatSessionContext';
import { AuthProvider } from './contexts/AuthContext';
import { UserSettingsProvider } from './contexts/UserSettingsContext';
import './index.css'; // Ensure this includes the global styles to reset margins and paddings

import { APP_VERSION } from './version';

// Version check on initial load (before rendering the app)
const checkAppVersion = () => {
  // Don't perform version checking during logout
  if (localStorage.getItem('logging_out') === 'true') {
    return true;
  }

  const cachedVersion = localStorage.getItem('appVersion');

  // Check if we came from a version reload
  const urlParams = new URLSearchParams(window.location.search);
  const fromReload = urlParams.get('versionReload') === 'true';

  if (fromReload) {
    // Coming from a reload - clean up URL and update stored version
    window.history.replaceState({}, document.title, window.location.pathname);
    localStorage.setItem('appVersion', APP_VERSION);
    return true;
  }

  // Normal page load - check version
  if (cachedVersion !== APP_VERSION) {
    // Force a hard reload with cache clearing
    const reloadUrl = `${window.location.pathname}?versionReload=true&t=${Date.now()}`;
    localStorage.setItem('pendingVersionUpdate', APP_VERSION);

    // Use a small timeout to ensure this message is seen in console
    setTimeout(() => {
      window.location.replace(reloadUrl);
    }, 50);
    return false;
  }

  return true;
};

// Execute the check before rendering
if (checkAppVersion()) {

  // Get the root element to render the React app
  const root = ReactDOM.createRoot(document.getElementById('root'));

  // Render the app with necessary providers
  root.render(
    <AuthProvider>
      <UserSettingsProvider>
        <ThemeContextProvider>
          <ChatSessionProvider>
            <MessageHandlerProvider>
              <DndProvider backend={HTML5Backend}>
                <App />
              </DndProvider>
            </MessageHandlerProvider>
          </ChatSessionProvider>
        </ThemeContextProvider>
      </UserSettingsProvider>
    </AuthProvider>
  );
}